import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { Stack } from "@mui/material";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { APP_ICONS } from "../../../../assets";
import PaperContainer from "../../../../components/Containers/PaperContainer";
import GlobalButton from "../../../../components/CustomButton";
import DynamicPaginatedTable from "../../../../components/Tables/DynamicPaginatedTable";
import TextInput from "../../../../components/TextInputs";
import useDebounce from "../../../../hooks/useDebounce";
import {
  getAllPromoCodes,
  updatePromoCode,
} from "../../../../services/promoCodeApiFn";
import { handleOpenSnackBar } from "../../../../utils";
import { AddEditPromoCodeDialog } from "./AddEditPromoCodeDialog";
import {
  setPromoCodeCurrentPage,
  setPromoCodePageLimit,
  setPromoCodeTotalCount,
  setPromoCodeTotalPages,
} from "../../../../lib/slices/pagination/promoCodePageSlice";
import { setPromoCode } from "../../../../lib/slices/searchKeysSlice";

// Header Data For Promotional Block Table
const headerData = [
  "createdAt",
  "code",
  "description",
  "value",
  "maxDiscountAmount",
  "minOrderAmount",
  "usageLimit",
  "usedCount",
  "duration",
  "status",
  "actions",
];

// Table Rows For Promotional Block Table
const tableRows = [
  "created_date",
  "promo_code",
  "promo_code_description",
  "promo_code_value",
  "promo_code_max_discount",
  "promo_code_min_order",
  "promo_code_usage_limit",
  "promo_code_used_count",
  "promo_code_duration",
  "status",
  "actions",
];

// Promotional Block Component
export function PromoCode() {
  // Dispatch Hook
  const dispatch = useDispatch();
  // Open Dialog Ref
  const openDialog = useRef(null);
  // Loading States
  const [isLoading, setIsLoading] = useState(true);
  // All Promotional Blocks
  const [allPromoCodes, setAllPromoCodes] = useState([]);
  // Translation Hook
  const { t } = useTranslation(["catalogue", "common"]);
  // Search Keys
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  // Get Pagination for Promotional Blocks
  const pagination = useSelector((state) => state.promoCodePaginationReducer);
  // Debounced Search Term
  const debouncedSearchTerm = useDebounce(
    searchKeys.promoCode,
    1000,
    true,
    () => {
      dispatch(setPromoCodeCurrentPage(1));
    },
  );

  // Fetch All Brands
  const handleFetchAllPromoCodes = async (
    search,
    page,
    limit,
    loading = true,
  ) => {
    try {
      const params = {
        search,
        page,
        limit,
      };
      setIsLoading(loading);
      const response = await getAllPromoCodes(params);
      if (response.data.status === "success") {
        let data = response?.data.data?.data;
        const pagination = response?.data.data.pagination;
        setAllPromoCodes(data);
        dispatch(setPromoCodePageLimit(pagination.limit));
        dispatch(setPromoCodeCurrentPage(pagination.page));
        dispatch(setPromoCodeTotalCount(pagination.total));
        dispatch(setPromoCodeTotalPages(pagination.totalPages));
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch All Promotional Blocks On Component Mount
  useEffect(() => {
    handleFetchAllPromoCodes(
      debouncedSearchTerm,
      pagination.page,
      pagination.limit,
    );
  }, [debouncedSearchTerm, pagination.page, pagination.limit]);

  // Handle Edit Promotional Block
  const handleEdit = (params) => {
    openDialog.current.openDialog({
      type: "edit",
      data: params.row,
      getData: handleFetchAllPromoCodes,
    });
  };

  // handle page change
  const handlePageChange = (event, newPage) => {
    const page = newPage + 1;
    dispatch(setPromoCodeCurrentPage(page));
  };

  // handle rows per page change
  const handleRowsPerPageChange = (event) => {
    console.log(event, "event");
    const limit = parseInt(event.target.value, 10);
    dispatch(setPromoCodePageLimit(limit));
    dispatch(setPromoCodeCurrentPage(1));
  };

  // Handle Change Status Promotional Block
  const handleChangeStatus = async (event, row, key) => {
    try {
      let payload = {
        [key]: event,
      };
      const response = await updatePromoCode(row._id, payload);
      if (response.data.status === "success") {
        handleFetchAllPromoCodes(
          searchKeys.brand,
          pagination.page,
          pagination.limit,
          false,
        );
        handleOpenSnackBar("success", response.data.message);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    }
  };

  // Prepare Export Data For Promotional Block
  const prepareExportData = () => {
    return allPromoCodes?.map((item) => ({
      "Created At": moment(item.createdAt).format("MM-DD-YYYY") || "-",
      "Updated At": moment(item.updatedAt).format("MM-DD-YYYY") || "-",
      "Name In Romanian": item.title?.ro || "-",
      "Name In English": item.title?.en || "-",
      Status: item.status ? "Active" : "Inactive",
    }));
  };

  const renderHeaderAction = () => {
    return (
      <Stack
        gap={1}
        flexGrow={1}
        direction={"row"}
        alignItems={"center"}
        justifyContent={"flex-end"}
      >
        <GlobalButton
          variant={"contained"}
          btnText={"addNew"}
          handleClickBtn={() => {
            openDialog.current.openDialog({
              type: "add",
              data: null,
              getData: handleFetchAllPromoCodes,
            });
          }}
          styleProps={{
            width: "auto",
            height: 40,
          }}
        />
        <CSVLink
          data={prepareExportData()}
          filename={"promo-code.csv"}
          className="csv-link"
          target="_blank"
        >
          <GlobalButton
            variant={"export_button"}
            btnText={"export"}
            startIcon={<img src={APP_ICONS.exportBlockIcon} width={20} />}
            styleProps={{
              width: "auto",
              height: 40,
            }}
          />
        </CSVLink>
      </Stack>
    );
  };

  const renderSearchAction = () => {
    return (
      <Stack p={2} direction={{ md: "row" }} bgcolor={"primary.light"}>
        <TextInput
          styleProps={{ width: { xs: "100%", md: "35%", xl: "30%" } }}
          InputStartIcon={<img src={APP_ICONS.searchGreyIcon} width={25} />}
          InputEndIcon={
            searchKeys.promoCode && (
              <CancelOutlinedIcon
                fontSize="small"
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  dispatch(setPromoCode(""));
                }}
              />
            )
          }
          placeholder={t("searchByCode", { ns: "common" })}
          value={searchKeys.promoCode}
          onChange={(e) => {
            dispatch(setPromoCode(e.target.value));
          }}
        />
      </Stack>
    );
  };

  const renderTable = useMemo(() => {
    return (
      <DynamicPaginatedTable
        minWidth={1500}
        tableHeader={headerData}
        displayRows={tableRows}
        tableData={allPromoCodes || []}
        showActions={true}
        showEdit={true}
        handleEdit={handleEdit}
        handleChangeStatus={(e, row, key) =>
          handleChangeStatus(e, row, "status")
        }
        paginatedData={pagination}
        showDynamicPagination={true}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[10, 20, 30, 50, 100]}
        isLoading={isLoading}
      />
    );
  }, [allPromoCodes, isLoading]);

  return (
    <React.Fragment>
      <PaperContainer
        title={t("promoCode")}
        subtitle={t("viewPromoCode")}
        headerActions={renderHeaderAction()}
        searchActions={renderSearchAction()}
      >
        {renderTable}
      </PaperContainer>

      <AddEditPromoCodeDialog ref={openDialog} />
    </React.Fragment>
  );
}
